// common
import {
  getModule,
  VuexModule,
  Mutation,
  Action,
  Module,
} from "vuex-module-decorators";
import store from "@/store";

// api
import { getSalesConfigurations } from "@/api/gql/sales-configurations";

// modules
import UserModule from "@/store/modules/user";

// helpers
import {
  IGrid,
  IPaymentConfig,
  IPOSConfig,
  IPOSConfigStatus,
  PaymentMethodTypes,
} from "@/types/db/pos-config";
import { toLocalPOSConfig } from "@/utils/helpers/pos-config";
import { getGroupCurrenciesExchange, getGroupInfo } from "@/api/gql/group";
import { POSConfigErrors } from "@/constants/errors/pos-config";
import { GroupInfo } from "@/types/gql/group";
import { IPOSConfigState } from "@/interfaces/store/IPOSConfig";
import AppModule from "./app";
import { Currencies, Currency } from "@/constants/currency";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { OpenCashDrawerOptions } from "@/interfaces/cash-drawer";
import CashFloatModule from "./cash-float";
import { setElementPlusWeekStart } from "@/utils/helpers/dates";
import { CompanyLegalInfo } from "@smeetz/compliance-common";
import { getCompanyLegalInfo } from "@/utils/helpers/compliance";
import { LogService } from "@/services/logging";
import { isBelgianCompliant } from "@/utils/vue-helpers/compliance";

@Module({ dynamic: true, namespaced: true, store, name: "Configuration" })
class Configuration extends VuexModule {
  public loading = false;
  // Used to show a dynamic progress bar when loading config selected
  public pcsLoaded = 0;
  // used to show a dynamic progress bar when loading config selected
  public timeslotsLoaded = 0;
  // used to track progress of remaining steps (seating plans, promocodes, offers, etc.)
  public extraProgress = 0;
  public posConfigs: IPOSConfig[] = [];
  public selectedConfig: IPOSConfig | null = null;
  public userId = "";
  public groupInfo: GroupInfo | null = null;
  public groupCurrencies: Currency[] = [];
  public companyLegalInfo: CompanyLegalInfo | null = null;
  public selectedGrid: IGrid | null = null;
  public loadingText = "";
  private paymentSettings: any = [
    {
      id: PaymentMethodTypes.Cash,
      name: String(t("button.cash")),
      used: true,
      terminal: null,
    },
    {
      id: PaymentMethodTypes.CardManual,
      name: `${t("button.card")} ${t("button.manual-processing")}`,
      used: true,
      terminal: null,
    },
  ]; // Assigning a basic value to make the variable reactive for dev/test/Hangar Y
  public defaultPaymentMethodOverriden = false;

  public completeBookingWithBalance = false;
  public openCashDrawer: OpenCashDrawerOptions =
    OpenCashDrawerOptions.AFTER_CASH_PAYMENT;
  public autoScanTickets = false;

  public configPictureUrl = "";

  public get getLoadingText() {
    if (this.loadingText) {
      return String(t(`posconfiguration.${this.loadingText}`));
    }
    return String(t("posconfiguration.downloading"));
  }

  public get getPaymentSettings() {
    // move card payment method  the first index for fliplab
    const sortedPaymentSettings = [...this.paymentSettings];
    if (this.isFlipLab && !this.defaultPaymentMethodOverriden) {
      const length = sortedPaymentSettings.length;
      for (let i = 0; i < length; i++) {
        const current = sortedPaymentSettings[i];
        if (current.id == PaymentMethodTypes.CardAuto && current.used) {
          sortedPaymentSettings.splice(i, 1);
          sortedPaymentSettings.unshift(current);
          break;
        }
      }
    }
    return sortedPaymentSettings;
  }
  public get isFlipLab() {
    if (this.groupId) {
      return [16342 /* TEST */, 1, 19319, 19543, 19544, 19545, 19546].includes(
        this.groupId
      );
    }
    return false;
  }

  public get isVHS() {
    if (
      this.groupId &&
      [19066 /* demo */, 19469 /*VHS*/, 16342 /* Test */].includes(this.groupId)
    ) {
      return true;
    }
    return false;
  }

  public get isEdeis() {
    if (
      this.groupId &&
      [1, 19695, 19696, 19699, 19700].includes(this.groupId)
    ) {
      return true;
    }
    return false;
  }

  public get isLocarno() {
    if (this.groupId && [16342 /* Test */, 19017].includes(this.groupId)) {
      return true;
    }
    return false;
  }

  public get isEclipsoLyon() {
    if (
      import.meta.env.VITE_ENV === "dev" ||
      import.meta.env.VITE_ENV === "test" ||
      (this.groupId && [1, 19572].includes(this.groupId))
    ) {
      return true;
    }
    return false;
  }

  public get getAutoScanTickets(): boolean {
    return this.autoScanTickets;
  }

  public get groupId(): null | number {
    let config = this.selectedConfig;
    // if no config is selected, set it to be the first available pos config
    if (!config && this.posConfigs.length) {
      if (this.posConfigs) {
        config = this.posConfigs[0];
      }
    }
    const gid = AppModule.gid;
    const userModuleGID = UserModule.getUserGroupId;

    // assign group id with order of priority
    // 1- appmodule gid if the groupid was forced (mainly admin accounts)
    // 2- group id from the user module fetched upon login
    // 3- if none of the above is available then we set it from the available pos configs
    // 4- otherwise group id is null
    return gid || userModuleGID || config?.group || null;
  }

  public get getPosConfigPermission(): boolean | undefined {
    return (
      this.selectedConfig?.users?.some((item) => item === UserModule.userId) ||
      UserModule.isAdmin
    );
  }

  public get getSelectedConfig() {
    return this.selectedConfig;
  }

  public get groupPhoneCode() {
    return this.groupInfo?.brandingList?.[0]?.country?.alpha2 || "CH";
  }

  public get groupTerminals(): string[] {
    const groupInfo = this.groupInfo;

    if (!groupInfo) {
      return [];
    }

    return groupInfo.adyenTerminals || [];
  }

  public get configGrids(): IGrid[] {
    const posConfig = this.selectedConfig;
    if (posConfig) {
      return posConfig.gridsInfo?.grids || [];
    }

    return [];
  }

  public get sortedGrids(): IGrid[] {
    return this.configGrids.sort((a, b) => a.position - b.position);
  }

  public get firstGrid(): IGrid {
    return this.sortedGrids[0];
  }

  /**
   * POS terminal for credit card payments
   */
  public get getPosTerminal(): string {
    const selectedTerminal = this.paymentSettings.find(
      (p: any) => p.id === "cardAuto" || p.terminal
    );
    return (
      selectedTerminal.terminal ||
      this.selectedConfig?.paymentConfig?.terminal ||
      "V400cPlus-450462508"
    );
  }

  public get getGroupCurrency() {
    return this.groupInfo?.currency || Currencies.CHF;
  }

  public get getPaymentConfig(): IPaymentConfig {
    return (
      this.getSelectedConfig?.paymentConfig || {
        cash: { used: true },
        cardAuto: { used: false },
        cardManual: { used: false },
        terminal: "",
        invoice: { used: false },
        toBeInvoiced: { used: false },
        bankTransfer: { used: false },
        cheque: { used: false },
        chequeVacances: { used: false },
        chequeCulture: { used: false },
        passCulture: { used: false },
        chorus: { used: false },
        CE: { used: false },
        OCCYG: { used: false },
        couponSport: { used: false },
        posReka: { used: false },
        diverseVoucher: { used: false },
        CFF: { used: false },
        CGN: { used: false },
        QOQA: { used: false },
        DEINDEAL: { used: false },
        REALDEAL: { used: false },
        EXTERNAL_INCERT_VOUCHER: { used: false },
        Swiss_QR_Code_Bank_Transfer: { used: false },
        buyClub: { used: false },
        POS_BIMPLI_CADO: { used: false },
        POS_KADEOS_INFINI: { used: false },
        POS_ANCV_CONNECT: { used: false },
        TOUT_UN_CANTON: { used: false },
        CHAPLINS: { used: false },
        HELLO_CSE: { used: false },
        Z_CH_GUTSCHEIN: { used: false },
      }
    );
  }

  /**
   * Whether or not the customer wants to display a product category with many prices as separated button
   * or he still prefers to list the prices inside the PCSelect popup
   */
  public get isSeperatedButtons(): boolean {
    if (this.selectedGrid)
      return this.selectedGrid?.isPricesSeparatedButton ? true : false;
    else return false;
  }

  public get getGroupCountryCode(): string {
    const code = this.groupInfo?.brandingList?.[0]?.country?.alpha2 || "";
    return code;
  }

  /**
   * Belgium requires cash totals to be rounded to the nearest 5 cents, with the
   * difference booked as its own payment line.
   */
  public get requiresCashRounding(): boolean {
    return isBelgianCompliant.value;
  }

  /**
   * Whether or not the group has skidata integration
   */
  public get hasSkidataIntegration(): boolean {
    const skidataHandshakeList = this.groupInfo?.skidataHandshakeList || [];
    return skidataHandshakeList.length > 0;
  }

  public get maxBookingTabs(): number {
    if (
      import.meta.env.VITE_ENV === "dev" ||
      import.meta.env.VITE_ENV === "test"
    ) {
      return 20;
    }
    if (
      this.groupId &&
      [1, 19066, /*Fort de Chillon*/ 19657].includes(this.groupId)
    ) {
      return 20;
    }
    return 4;
  }

  @Mutation
  public restore(moduleFields: Partial<IPOSConfigState>) {
    const {
      selectedConfig,
      selectedGrid,
      groupInfo,
      posConfigs,
      paymentSettings,
      completeWithBalance,
      openCashDrawer,
      autoScanTickets,
      defaultPaymentMethodOverriden,
      groupCurrencies,
      configPictureUrl,
      companyLegalInfo,
    } = moduleFields;

    if (selectedConfig) {
      this.selectedConfig = selectedConfig;
    }
    if (selectedGrid) {
      this.selectedGrid = selectedGrid;
    }
    if (groupInfo) {
      this.groupInfo = groupInfo;
    }
    if (companyLegalInfo) {
      this.companyLegalInfo = companyLegalInfo;
    }
    if (posConfigs) {
      this.posConfigs = posConfigs;
    }
    if (paymentSettings) {
      this.paymentSettings = paymentSettings;
    }
    if (completeWithBalance) {
      this.completeBookingWithBalance = completeWithBalance;
    }
    if (openCashDrawer) {
      this.openCashDrawer = openCashDrawer;
    }
    if (autoScanTickets) {
      this.autoScanTickets = autoScanTickets;
    }
    if (defaultPaymentMethodOverriden) {
      this.defaultPaymentMethodOverriden = defaultPaymentMethodOverriden;
    }
    if (groupCurrencies) {
      this.groupCurrencies = groupCurrencies;
    }
    if (!groupCurrencies) {
      // this could occur if the app updates but users don't reload their configs to fetch group currencies
      // this should be removed when all users have transitioned into multi-currencies logic
      const currencyLabel = (this.groupInfo?.currency ||
        Currencies.CHF) as Currencies;
      const currency: Currency = {
        id: 0,
        isDefault: 1,
        status: 1,
        label: currencyLabel,
        exchangeRate: 1,
      };
      this.groupCurrencies = [currency];
    }
    if (configPictureUrl) {
      this.configPictureUrl = configPictureUrl;
    }
    if (companyLegalInfo) {
      this.companyLegalInfo = companyLegalInfo;
    }
  }

  @Mutation
  public selectGrid(grid: IGrid | null) {
    this.selectedGrid = grid;
  }

  @Mutation
  public setPCsLoaded(count: number) {
    this.pcsLoaded = count;
  }

  @Mutation
  public setLoadingText(loading: string) {
    this.loadingText = loading;
  }

  @Mutation
  public setTimeslotsLoaded(count: number) {
    this.timeslotsLoaded = count;
  }

  @Mutation
  public setExtraProgress(count: number) {
    this.extraProgress = count;
  }

  /**
   * Set the payment settings array
   */
  @Mutation
  public setPaymentSettings(paymentSettings: any) {
    const currentCardPaymentMethod = this.paymentSettings.find(
      (paymentMethod: any) => {
        return paymentMethod.id === PaymentMethodTypes.CardAuto;
      }
    );
    const newCardPaymentMethod = paymentSettings.find((paymentMethod: any) => {
      return paymentMethod.id === PaymentMethodTypes.CardAuto;
    });
    if (!currentCardPaymentMethod?.used && newCardPaymentMethod?.used) {
      this.defaultPaymentMethodOverriden = false;
    } else {
      this.defaultPaymentMethodOverriden = true;
    }
    this.paymentSettings = paymentSettings;
  }

  @Action({ rawError: true })
  public async fetchUserConfigs(userGroupId: number) {
    const posConfigs = await getSalesConfigurations(userGroupId, [
      IPOSConfigStatus.Active,
    ]);
    if (posConfigs) {
      // First of all lets remove Configs that are not assigned to the current user
      const userId = UserModule.userId;
      const posConfigsFiltered = [];
      for (const posConfig of posConfigs) {
        if (posConfig.users && posConfig.users.includes(String(userId))) {
          posConfigsFiltered.push(posConfig);
        } else {
          // This config is not assigned to the current user, we do not show it on UI
          // console.log("This config is not assigned to the current user, we do not show it on UI");
          // console.log(posConfig);
          continue;
        }
      }
      const configs = UserModule.canSeeAllPOSConfigs
        ? posConfigs
        : posConfigsFiltered;
      // remove configs without gridsInfo and map them to local representation
      this.SET_CONFIGS(
        configs
          .filter((conf) => !!conf.gridsInfo)
          .map((conf) => toLocalPOSConfig(conf))
      );
    }
  }

  @Action({ rawError: true })
  public async fetchGroupInfo() {
    const groupId = this.groupId;
    if (!groupId) {
      throw new Error(POSConfigErrors.NoPOSConfigSelected);
    }

    const groupInfo = await getGroupInfo(groupId);
    this.setGroupInfo(groupInfo);

    // if the week start setting from b2b changes
    setElementPlusWeekStart();

    const groupCurrencies = await getGroupCurrenciesExchange(groupId);
    this.setGroupCurrencies(groupCurrencies);

    // company legal info for belgium if needed
    try {
      const companyInfo = await getCompanyLegalInfo();
      if (companyInfo) {
        this.setCompanyLegalInfo(companyInfo);
      }
    } catch (error) {
      LogService.info("Error fetching company legal info", { error });
    }
    return groupInfo;
  }

  @Action({ rawError: true })
  public postPaymentMethods(payload: IPaymentConfig) {
    // @ts-ignore
    const config: IPOSConfig = {
      ...this.selectedConfig,
      paymentConfig: payload,
    };

    this.SET_SELECTED_CONFIG(config);
  }

  @Action({ commit: "SET_SELECTED_CONFIG" })
  public removePOSConfig() {
    return "";
  }

  @Mutation
  private SET_CONFIGS(payload: IPOSConfig[]) {
    this.posConfigs = payload;
  }

  @Mutation
  public SET_SELECTED_CONFIG(payload: IPOSConfig | null) {
    this.selectedConfig = payload;
  }

  @Mutation
  public setGroupInfo(groupInfo: GroupInfo) {
    this.groupInfo = groupInfo;
  }

  @Mutation
  public setGroupCurrencies(currencies: Currency[]) {
    // if currency was disabled during cash sales, we assume its status is still active
    const startAmounts = CashFloatModule.cashFloat?.startAmounts || {};
    const amounts = CashFloatModule.cashFloat?.amounts || {};
    const currenciesAlreadyInStartAmounts: Currencies[] = [];
    for (const curr in startAmounts) {
      const currency = curr as Currencies;
      currenciesAlreadyInStartAmounts.push(currency);
    }
    // currencies that were disabled when sales started (not present in startAmounts), then enabled during, should be added to cashfloat
    currencies.forEach((currency) => {
      if (
        currency.status === 1 &&
        typeof startAmounts[currency.label] === "undefined"
      ) {
        startAmounts[currency.label] = 0;
        amounts[currency.label] = 0;
      }
    });
    // set group currencies
    this.groupCurrencies = currencies.filter(
      (currency) =>
        currency.status === 1 ||
        currenciesAlreadyInStartAmounts.includes(currency.label)
    );
  }

  @Mutation
  public setCompleteBookingWithBalance(status: boolean) {
    this.completeBookingWithBalance = status;
  }

  @Mutation
  public setOpenCashDrawer(value: OpenCashDrawerOptions) {
    this.openCashDrawer = value;
  }

  @Mutation
  public setAutoScanTickets(value: boolean) {
    this.autoScanTickets = value;
  }

  @Mutation
  public setConfigPictureUrl(value: string) {
    this.configPictureUrl = value;
  }

  @Mutation
  public setCompanyLegalInfo(companyInfo: CompanyLegalInfo) {
    this.companyLegalInfo = companyInfo;
  }
}

const POSConfigModule = getModule(Configuration);
export default POSConfigModule;
